import {
  Stack,
  Button,
  Title,
  Text,
  Group,
  Skeleton,
  Box,
} from '@mantine/core';
import cn from 'clsx';
import BigNumber from 'bignumber.js';
import { IoArrowDownSharp } from 'react-icons/io5';
import { useState } from 'react';
import { ContentCard } from 'src/components/ContentCard/ContentCard';
import {
  NumberInput,
  NumberInputProps,
} from 'src/components/NumberInput/NumberInput';
import { WalletSuspense } from 'src/components/WalletSuspense/WalletSuspense';
import { ErrorDisplay } from 'src/components/ErrorDisplay/ErrorDisplay';
import { Rows } from 'src/components/Rows/Rows';
import { Token } from 'src/components/Token/Token';
import { formatDecimal } from 'src/utils';
import { useProtocolInfo } from 'src/api';
import {
  useAssetsToSharesRatio,
  useStakingIsPaused,
} from 'src/api/smart-contracts';
import { SHOW_GAS_FEES, NETWORK_VARS } from 'src/constants';
import { useMakeDeposit } from 'src/hooks/useMakeDeposit';

const inputCommonProps: Partial<NumberInputProps> = {
  inputProps: {
    size: 'sm',
    labelProps: { w: '100%' },
    classNames: {
      root: 'bg-[#151519] rounded-[16px] border-none px-3 py-4',
      label: 'px-2 mb-3',
      wrapper: '!px-2 bg-transparent',
      input:
        'h-[32px] pl-[40px] border-none bg-transparent text-[#A0A0B1] text-[24px] font-[700]',
    },
  },
  leftSectionProps: {
    className: 'px-2 h-[32px] w-[48px]',
  },
};

const SHOW_STATS = import.meta.env.VITE_SHOW_STATS === 'true';

export function StakePanel({ apy }: { apy?: React.ReactNode }) {
  const [value, setValue] = useState<string | undefined>();
  const [receiveValue, setReceiveValue] = useState<string | undefined>();
  // const dollarValue = BigNumber(receiveValue || 0).multipliedBy(123.456);
  const valueBN = BigNumber(value || 0);

  const { protocolInfo, isLoadingProtocolInfo } = useProtocolInfo({
    enabled: SHOW_STATS,
  });
  const { stakingIsPaused, stakingIsPausedMessage } = useStakingIsPaused();
  const { assetsToSharesRatio, sharesToAssetsRatio, isLoadingRatio } =
    useAssetsToSharesRatio();

  const {
    isLoadingBalance,
    balance,
    isDepositing,
    depositError,
    transactionCost,
    isLoadingTransactionCost,
    notEnoughBalanceForGas,
    makeDeposit,
  } = useMakeDeposit({
    amount: value || '0',
    onSuccess: () => {
      setValue(undefined);
      setReceiveValue(undefined);
    },
  });

  const handleValueChange = (newValue?: string) => {
    setValue(newValue);
    setReceiveValue(
      BigNumber(assetsToSharesRatio)
        .multipliedBy(newValue || 0)
        .toFixed()
    );
  };

  const handleReceiveValueChange = (newValue?: string) => {
    setReceiveValue(newValue);
    setValue(
      BigNumber(sharesToAssetsRatio)
        .multipliedBy(newValue || 0)
        .toFixed()
    );
  };

  const isInsufficientBalance = BigNumber(valueBN).gt(balance?.formatted || 0);

  const getSubmitErrorMessage = () => {
    if (BigNumber(valueBN).lte(0)) {
      return 'Enter an amount';
    }

    if (notEnoughBalanceForGas) {
      return 'Insufficient balance to cover gas fees';
    }

    if (isInsufficientBalance) {
      return 'Insufficient balance';
    }

    return '';
  };

  const submitErrorMessage = getSubmitErrorMessage();
  const submitDisabled =
    stakingIsPaused || !!submitErrorMessage || !makeDeposit;

  const handleSubmit = () => {
    if (submitDisabled) {
      return;
    }

    makeDeposit();
  };

  return (
    <ContentCard
      cardProps={{ className: isDepositing ? 'pointer-events-none' : '' }}
    >
      <Group justify="space-between" mb={24}>
        <Title order={2} fz={24} fw={600} lh={1.5}>
          Stake MON
        </Title>

        {apy}
      </Group>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
        className={cn(stakingIsPaused && 'pointer-events-none')}
      >
        <Stack gap={24} className="relative">
          <Stack gap={6} className="relative">
            <NumberInput
              value={value}
              label={
                <Group justify="space-between" w="100%">
                  <Text>MON amount</Text>
                  <Token.Balance
                    tokenInfo={{
                      contract_address: NETWORK_VARS.tokensAddresses.MON,
                      decimals: 18,
                      symbol: 'MON',
                    }}
                    color={isInsufficientBalance ? '#FF3E55' : undefined}
                  />
                </Group>
              }
              tokenSymbol="__MON__"
              {...inputCommonProps}
              onChange={handleValueChange}
              onMaxClick={() => handleValueChange(balance?.formatted || '0')}
            />

            <Box
              className={cn(
                'absolute left-[50%] top-[50%] ml-[-18px] mt-[-18px]',
                'flex items-center justify-center rounded-full bg-[#0F1012]'
              )}
              w={36}
              h={36}
              mx="auto"
            >
              <div
                className={cn(
                  'flex justify-center items-center w-6 h-6 border-[2px] border-solid rounded-full',
                  submitDisabled ? 'border-[#3E3E4C]' : 'border-primarybrand'
                )}
              >
                <IoArrowDownSharp
                  size={16}
                  className={cn(
                    'mt-[-1px]',
                    submitDisabled ? 'text-[#3E3E4C]' : 'text-primarybrand'
                  )}
                />
              </div>
            </Box>

            <NumberInput
              value={receiveValue}
              label="You will receive aprMON"
              tokenSymbol="__APRMON__"
              rightSectionWidth={260}
              isLoading={isLoadingRatio}
              {...inputCommonProps}
              onChange={handleReceiveValueChange}
            />
          </Stack>

          <Stack gap={16}>
            <WalletSuspense buttonProps={{ size: 'lg' }}>
              <Button
                variant="gradient"
                size="lg"
                type="submit"
                loading={isDepositing}
                disabled={submitDisabled}
              >
                {stakingIsPaused
                  ? stakingIsPausedMessage
                  : submitErrorMessage || 'Stake'}
              </Button>

              {!submitErrorMessage && (
                <ErrorDisplay>{depositError?.message}</ErrorDisplay>
              )}
            </WalletSuspense>

            <Stack gap={8}>
              <Rows
                items={[
                  {
                    label: 'Exchange rate',
                    value: isLoadingRatio ? (
                      <Skeleton w={160} h={23} />
                    ) : (
                      <>
                        1 MON ={' '}
                        {formatDecimal(assetsToSharesRatio, {
                          withTruncate: true,
                        })}{' '}
                        aprMON
                      </>
                    ),
                  },
                  ...(SHOW_GAS_FEES
                    ? [
                        {
                          label: 'Estimated transaction cost',
                          value: isLoadingTransactionCost ? (
                            <Skeleton w={160} h={23} />
                          ) : transactionCost.eq(0) ? (
                            '-'
                          ) : (
                            <>
                              {formatDecimal(transactionCost, {
                                withTruncate: true,
                              })}{' '}
                              MON
                            </>
                          ),
                        },
                      ]
                    : []),
                ]}
              />
            </Stack>
          </Stack>
        </Stack>
      </form>
    </ContentCard>
  );
}
